import { useOrdersStore } from "../stores/useOrdersStore";
import { InfiniteScrollList } from "../components/core/InfiniteScrollList";
import { OrderListCard } from "../components/list-cards/orders/OrderListCard";
import { OrderListCardSkeleton } from "../components/list-cards/orders/OrderListCardSkeleton";
import { useTranslation } from "react-i18next";
import { ordersApi } from "../api/orders.api";
import { Title } from "@/components/core/Title";
import { PageContainer } from "@/components/core/PageContainer";
import { useRefreshOnLocationChange } from "@/hooks/useRefreshOnLocationChange";
import { ActiveLocationBadges } from "@/components/blocks/ActiveLocationBadges";
import { Separator } from "@/components/ui/separator";
import { useIndexedDBHealthCheck } from "@/hooks/useIndexedDBHealthCheck";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { useOrdersCalculator } from "@/hooks/useOrdersCalculator";
import { OrdersCalculatorFooter } from "@/components/blocks/OrdersCalculatorFooter";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { 
  getOrderId, 
  getListBottomPadding, 
  getCalculatorButtonStyles 
} from "@/utils/orders.utils";
import { cn } from "@/lib/utils";

export function Orders() {
  const { t } = useTranslation();
  const store = useOrdersStore();

  useRefreshOnLocationChange(store);

  useIndexedDBHealthCheck({
    enabled: true,
    backgroundThreshold: 300000,
    onUnhealthy: () => {
      console.warn("IndexedDB unhealthy detected on Orders page");
    },
  });

  const calculator = useOrdersCalculator();
  const totals = calculator.getCalculatedTotals();
  const listPaddingClass = getListBottomPadding(calculator.isCalculatorMode);
  const calculatorButtonClass = getCalculatorButtonStyles(calculator.isCalculatorMode);

  return (
    <PageErrorBoundary onReset={() => store.refresh()}>
      <PageContainer asList className="relative">
        <Title>{t("locations.activeLocations")}</Title>

        <ActiveLocationBadges className="flex flex-col flex-wrap gap-2" />

        <Separator className="my-4" />

        <Title
          refreshAction={() => store.refresh()}
          actionButton={
            <Button
              variant="link"
              size="icon"
              onClick={calculator.toggleCalculatorMode}
              className={cn(calculatorButtonClass, "p-0")}
            >
              <Calculator className="!h-5 !w-5" />
            </Button>
          }
        >
          {t("orders.title")}
        </Title>

        <div className={cn("flex-1 min-h-0", listPaddingClass)}>
          <InfiniteScrollList
            store={store}
            renderItem={OrderListCard}
            renderSkeleton={OrderListCardSkeleton}
            queryKey={["orders"]}
            queryFn={ordersApi.fetchOrders}
            emptyState={{
              image: "/images/empty-states/orders-empty-state.png",
              title: t("orders.emptyState.title"),
            }}
            enablePolling
            isSelectable={calculator.isCalculatorMode}
            selectedItemIds={calculator.selectedOrderIds}
            onItemSelect={calculator.toggleOrderSelection}
            getItemId={getOrderId}
          />
        </div>

        {calculator.isCalculatorMode && (
          <OrdersCalculatorFooter
            totals={totals}
            selectedCount={calculator.selectedOrdersCount}
            onCancel={calculator.toggleCalculatorMode}
          />
        )}
      </PageContainer>
    </PageErrorBoundary>
  );
}
